import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { EventCard } from '../common/EventCard';
import { AddEventModal } from '../common/AddEventModal';
import { FlyerOcrModal } from '../common/FlyerOcrModal';
import { useLang } from '../../i18n/context';
import { useAuth } from '../../contexts/AuthContext';
import type { Event, Stats } from '../../types';

interface AgendaTabProps {
  stats: Stats | null;
  onScan: () => void;
  isScanning: boolean;
  onGoToRadar?: () => void;
}

export function AgendaTab({ stats, onScan, isScanning, onGoToRadar }: AgendaTabProps) {
  const { t } = useLang();
  const { isAdmin } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [allEvents, setAllEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeGenre, setActiveGenre] = useState('Tout');
  const [activeCity, setActiveCity] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFlyerModal, setShowFlyerModal] = useState(false);
  const [freeOnly, setFreeOnly] = useState(false);
  const [timeView, setTimeView] = useState<'upcoming' | 'past' | 'all'>('upcoming');
  const [activeRadar, setActiveRadar] = useState<any>(null);

  // Fetch the most recently scanned city radar for the banner
  useEffect(() => {
    api.getRadars().then(radars => {
      // Pick the one with most events, or most recently scanned
      const active = radars
        .filter((r: any) => r.event_count > 0)
        .sort((a: any, b: any) => (b.event_count || 0) - (a.event_count || 0))[0];
      setActiveRadar(active || null);
    }).catch(() => {});
  }, []);

  // Build genre list dynamically from current events (exclude non-genre tags)
  const NON_GENRES = new Set(['sound system', 'soundsystem', 'sound_system']);
  const availableGenres = React.useMemo(() => {
    const counts: Record<string, number> = {};
    allEvents.forEach(e => {
      (e.genre_tags || []).forEach(t => {
        if (!NON_GENRES.has(t.toLowerCase())) counts[t] = (counts[t] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([tag]) => tag.charAt(0).toUpperCase() + tag.slice(1));
  }, [allEvents]);

  // Top-5 cities from the current upcoming pool — drives the city-filter channel strips
  const topCities = React.useMemo(() => {
    const counts: Record<string, number> = {};
    allEvents.forEach(e => {
      const c = (e.city || '').trim();
      if (c) counts[c] = (counts[c] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([city, count]) => ({ city, count }));
  }, [allEvents]);
  const maxCityCount = Math.max(1, ...topCities.map(c => c.count));

  // Client-side city filter applied on top of the server-fetched list
  const displayedEvents = React.useMemo(() => {
    if (!activeCity) return events;
    return events.filter(e => (e.city || '').toLowerCase() === activeCity.toLowerCase());
  }, [events, activeCity]);

  useEffect(() => {
    loadEvents();
  }, [activeGenre, search, timeView]);

  async function loadEvents() {
    setLoading(true);
    try {
      const params: Record<string, string> = { status: 'validated', limit: '100' };
      if (activeGenre !== 'Tout') params.genre = activeGenre.toLowerCase();
      if (search) params.search = search;

      const today = new Date().toISOString().split('T')[0];
      if (timeView === 'upcoming') {
        params.from = today;
      } else if (timeView === 'past') {
        params.to = today;
        params.sort = 'desc'; // newest past events first (avoids LIMIT cutting recent ones)
      }
      // 'all' = no date filter

      const data = await api.getEvents(params);

      setEvents(data);
      // Store unfiltered events for genre computation (only when not filtering by genre)
      if (activeGenre === 'Tout' && !search) {
        setAllEvents(data);
      }
    } catch (err) {
      console.error('Failed to load events:', err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      {/* Stats Bar — compact cards + dub-console city-filter channel strips */}
      <div className="stats-bar">
        <div className="stat-card">
          <div className="stat-number">{stats?.upcoming_events ?? '-'}</div>
          <div className="stat-label">{t('agenda.upcoming_events')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats?.total_events ?? '-'}</div>
          <div className="stat-label">{t('agenda.total_events')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats?.total_venues ?? '-'}</div>
          <div className="stat-label">{t('agenda.venues_followed')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats?.active_sources ?? '-'}</div>
          <div className="stat-label">{t('agenda.active_sources')}</div>
        </div>

        {/* City Radar card (Boom Zoom) — kept compact on the right */}
        {activeRadar && onGoToRadar && (
          <div
            className="stat-card"
            onClick={onGoToRadar}
            style={{
              cursor: 'pointer',
              background: 'linear-gradient(135deg, rgba(201,168,76,0.1) 0%, rgba(76,175,80,0.06) 100%)',
              borderColor: 'rgba(201,168,76,0.35)',
            }}
          >
            <div className="stat-number" style={{ color: '#c9a84c', fontSize: '14px' }}>
              {({ DE: '\uD83C\uDDE9\uD83C\uDDEA', HU: '\uD83C\uDDED\uD83C\uDDFA', FR: '\uD83C\uDDEB\uD83C\uDDF7', ES: '\uD83C\uDDEA\uD83C\uDDF8' } as any)[activeRadar.country_code] || '\uD83C\uDF0D'} {activeRadar.event_count || 0}
            </div>
            <div className="stat-label" style={{ color: '#c9a84c' }}>
              {activeRadar.name}
            </div>
          </div>
        )}
      </div>

      {/* Time View Toggle */}
      <div className="filter-bar" style={{ marginBottom: '8px' }}>
        <button className={`filter-chip ${timeView === 'upcoming' ? 'active' : ''}`} onClick={() => setTimeView('upcoming')}>
          {t('agenda.upcoming')}
        </button>
        <button className={`filter-chip ${timeView === 'past' ? 'active' : ''}`} onClick={() => setTimeView('past')}>
          {t('agenda.past')}
        </button>
        <button className={`filter-chip ${timeView === 'all' ? 'active' : ''}`} onClick={() => setTimeView('all')}>
          {t('agenda.all')}
        </button>
      </div>

      {/* Genre Filters - dynamic from current events */}
      <div className="filter-bar">
        {['Tout', ...availableGenres].map(g => (
          <button
            key={g}
            className={`filter-chip ${activeGenre === g && !freeOnly ? 'active' : ''}`}
            onClick={() => { setActiveGenre(g); if (g === 'Tout') setFreeOnly(false); }}
          >
            {g}
          </button>
        ))}
        <div className="search-wrapper">
          <span className="search-icon">&#128269;</span>
          <input
            className="search-input"
            placeholder={t('agenda.search_placeholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>
      {/* Action row — FREE knob on left, city-fader console + Ajouter/Flyer OCR on right */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
        {/* FREE meter — only visible when free events exist */}
        {events.some(e => e.is_free || e.price_info?.toLowerCase().includes('gratuit') || e.price_info?.toLowerCase().includes('free')) && (
          <FreeKnob active={freeOnly} onClick={() => setFreeOnly(!freeOnly)} />
        )}
        <div style={{ flex: 1 }} />

        {/* City-filter mixer console — now inline with action buttons */}
        {topCities.length > 0 && (
          <div className="city-fader-console" role="group" aria-label={t('agenda.city_filter') || 'City filter'}>
            <span className="city-fader-console-screw tl" />
            <span className="city-fader-console-screw tr" />
            <span className="city-fader-console-screw bl" />
            <span className="city-fader-console-screw br" />
            {topCities.map(({ city, count }) => {
              const isActive = activeCity === city;
              const pct = Math.round(10 + (count / maxCityCount) * 80);
              return (
                <button
                  key={city}
                  type="button"
                  className="console-strip"
                  data-active={isActive}
                  onClick={() => setActiveCity(isActive ? null : city)}
                  title={`${city} — ${count} events`}
                >
                  <span className="console-strip-led" />
                  <span className="console-strip-count">{count}</span>
                  <div className="fader-track">
                    <div className="fader-knob" style={{ bottom: `${pct}%` }} />
                  </div>
                  <span className="console-strip-name">{city}</span>
                </button>
              );
            })}
          </div>
        )}

        {isAdmin && <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
          {t('agenda.add')}
        </button>}
        <button className="btn btn-primary" onClick={() => setShowFlyerModal(true)}>
          📷 {t('agenda.flyer_ocr')}
        </button>
      </div>

      {/* Section Header */}
      <div className="section-header">
        <div>
          <span className="section-title">
            {timeView === 'upcoming' ? t('agenda.upcoming_title') : timeView === 'past' ? t('agenda.past_title') : t('agenda.all_title')}
          </span>
          <span className="country-badge">BEL</span>
          {activeCity && (
            <span
              onClick={() => setActiveCity(null)}
              title="Clear city filter"
              style={{
                marginLeft: '8px', fontSize: '10px', fontWeight: 700, letterSpacing: '1px',
                padding: '2px 7px', borderRadius: '3px', cursor: 'pointer',
                background: 'rgba(201,168,76,0.15)', border: '1px solid var(--gold)',
                color: 'var(--gold)', textTransform: 'uppercase',
              }}
            >
              {activeCity} ✕
            </span>
          )}
          <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>
            {displayedEvents.length} {t('agenda.results')}
          </span>
          {activeCity && (
            <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic', opacity: 0.85 }}
              title={t('agenda.city_filter_warning')}>
              ⓘ {t('agenda.city_filter_warning')}
            </span>
          )}
        </div>
        {isAdmin && <button className="btn" onClick={onScan} disabled={isScanning}>
          {isScanning ? (
            <><span className="spinner" style={{ width: 14, height: 14, marginRight: 6 }}></span> {t('agenda.scanning')}</>
          ) : (
            <>&#8635; {t('agenda.scan_now')}</>
          )}
        </button>}
      </div>

      {/* Events Grid — displayedEvents applies activeCity filter on top of server results */}
      {loading ? (
        <div className="loading"><span className="spinner"></span> {t('common.loading')}</div>
      ) : displayedEvents.length === 0 ? (
        <div className="empty-state">
          <p>{t('agenda.no_events')}</p>
          <p style={{ marginTop: '8px', fontSize: '13px' }}>{t('agenda.launch_scan')}</p>
        </div>
      ) : (
        <div className="events-grid">
          {(freeOnly ? displayedEvents.filter(e => e.is_free || e.price_info?.toLowerCase().includes('gratuit') || e.price_info?.toLowerCase().includes('free')) : displayedEvents).map(event => (
            <EventCard key={event.id} event={event} onUpdate={loadEvents} />
          ))}
        </div>
      )}

      {showAddModal && (
        <AddEventModal onClose={() => setShowAddModal(false)} onCreated={loadEvents} />
      )}
      {showFlyerModal && (
        <FlyerOcrModal onClose={() => setShowFlyerModal(false)} onCreated={loadEvents} />
      )}
    </div>
  );
}

// ─── FREE Analog Meter Component ─────────────────────────────────────────────

function FreeKnob({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      title={active ? 'Show all events' : 'Show FREE events only'}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '6px 16px 5px', height: '54px',
        borderRadius: '8px', cursor: 'pointer', userSelect: 'none',
        background: active
          ? 'linear-gradient(180deg, #1a2e1a 0%, #0e1a0e 100%)'
          : 'linear-gradient(180deg, #1e1e22 0%, #111114 100%)',
        border: `1.5px solid ${active ? '#4caf50' : '#333'}`,
        boxShadow: active
          ? '0 0 14px rgba(76,175,80,0.4), inset 0 1px 0 rgba(255,255,255,0.05)'
          : 'inset 0 1px 0 rgba(255,255,255,0.04), 0 2px 4px rgba(0,0,0,0.3)',
        transition: 'all 0.3s',
        minWidth: '90px',
      }}
    >
      {/* Analog meter */}
      <div style={{
        width: '52px', height: '26px', position: 'relative',
        borderRadius: '26px 26px 0 0', overflow: 'hidden',
        background: active
          ? 'radial-gradient(ellipse at 50% 100%, #d4e8d0 0%, #a8c8a0 40%, #6a9a60 100%)'
          : 'radial-gradient(ellipse at 50% 100%, #d0ccc0 0%, #a8a498 40%, #6a6860 100%)',
        border: `1px solid ${active ? '#4caf5066' : '#44444466'}`,
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4)',
        transition: 'background 0.3s',
      }}>
        {/* Scale marks */}
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} style={{
            position: 'absolute', bottom: 0, left: '50%',
            width: '0.5px', height: i % 2 === 0 ? '6px' : '4px',
            background: '#333',
            transformOrigin: 'bottom center',
            transform: `rotate(${-60 + i * 30}deg)`,
          }} />
        ))}
        {/* Needle */}
        <div style={{
          position: 'absolute', bottom: '-1px', left: '50%',
          width: '1.5px', height: '22px', marginLeft: '-0.75px',
          background: active ? '#c62828' : '#333',
          borderRadius: '1px 1px 0 0',
          transformOrigin: 'bottom center',
          transform: `rotate(${active ? 50 : -50}deg)`,
          transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s',
          boxShadow: '0 0 1px rgba(0,0,0,0.5)',
        }} />
        {/* Pivot */}
        <div style={{
          position: 'absolute', bottom: '-3px', left: '50%',
          width: '6px', height: '6px', marginLeft: '-3px',
          borderRadius: '50%',
          background: 'radial-gradient(circle at 40% 35%, #888 0%, #333 100%)',
          border: '0.5px solid #555',
        }} />
      </div>
      {/* Label */}
      <span style={{
        fontSize: '9px', fontWeight: 800, letterSpacing: '2px',
        marginTop: '3px',
        color: active ? '#4caf50' : '#555',
        textShadow: active ? '0 0 6px rgba(76,175,80,0.5)' : 'none',
        transition: 'all 0.3s',
        textTransform: 'uppercase',
      }}>
        Free Events
      </span>
    </div>
  );
}
